/**
 * In-memory store for locally saved templates (not persisted between browser sessions).
 *
 * Used by the template builder to allow saving and re-editing templates
 * before they're committed to the server.
 */
import type { TemplateListItem } from './types';

const LOCAL_ID_PREFIX = 'local-';
let localIdCounter = 1;

const store = new Map<string, TemplateListItem>();
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((fn) => fn());
}

export function generateLocalId(): string {
  return `${LOCAL_ID_PREFIX}${localIdCounter++}`;
}

export function isLocalTemplateId(id: string): boolean {
  return id.startsWith(LOCAL_ID_PREFIX);
}

export function saveLocalTemplate(template: TemplateListItem): void {
  store.set(template.id, { ...template, lastUpdated: new Date().toISOString() });
  notifyListeners();
}

export function getLocalTemplate(id: string): TemplateListItem | undefined {
  return store.get(id);
}

export function getAllLocalTemplates(): TemplateListItem[] {
  return Array.from(store.values());
}

export function deleteLocalTemplate(id: string): void {
  store.delete(id);
  notifyListeners();
}

export function subscribeToLocalTemplates(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
